import React, { useState, useCallback, useMemo, Suspense } from "react";
import HouseScene from "@/components/experience/HouseScene";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Sparkles, Lightbulb, Snowflake, Fan, Blinds, Tv, Lock, Unlock, Camera, Moon, Sunrise, Film, LogOut, Home as HomeIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import usePageTitle from "@/lib/usePageTitle";

const initialState = {
    livingLights: true,
    livingFan: false,
    livingAc: false,
    livingTv: false,
    livingCurtains: true,
    bedroomLights: false,
    bedroomFan: false,
    bedroomAc: false,
    bedroomCurtains: false,
    doorLocked: true,
    camera: true,
    entranceLight: false,
    acTemp: 24,
};

const ASSISTANTS = [
    { id: "alexa", name: "Amazon Alexa", trigger: "Alexa," },
    { id: "google", name: "Google Home", trigger: "Hey Google," },
    { id: "siri", name: "Siri / Apple Home", trigger: "Hey Siri," },
    { id: "touch", name: "Touch Control", trigger: "" },
];

const COMMANDS = [
    { id: "lr_on", label: "Turn on living room lights", patch: { livingLights: true } },
    { id: "lr_off", label: "Turn off living room lights", patch: { livingLights: false } },
    { id: "br_on", label: "Turn on bedroom lights", patch: { bedroomLights: true } },
    { id: "br_off", label: "Turn off bedroom lights", patch: { bedroomLights: false } },
    { id: "ac22", label: "Set living room AC to 22°", patch: { livingAc: true, acTemp: 22 } },
    { id: "brac_on", label: "Turn on bedroom AC", patch: { bedroomAc: true } },
    { id: "brac_off", label: "Turn off bedroom AC", patch: { bedroomAc: false } },
    { id: "tv_on", label: "Turn on TV", patch: { livingTv: true } },
    { id: "tv_off", label: "Turn off TV", patch: { livingTv: false } },
    { id: "curt_open", label: "Open living room curtains", patch: { livingCurtains: true } },
    { id: "curt_close", label: "Close bedroom curtains", patch: { bedroomCurtains: false } },
    { id: "lock", label: "Lock the front door", patch: { doorLocked: true } },
    { id: "unlock", label: "Unlock the front door", patch: { doorLocked: false } },
    { id: "sec_on", label: "Turn on security mode", patch: { camera: true, doorLocked: true, entranceLight: true } },
];

const SCENES = [
    { id: "morning", label: "Good Morning", icon: Sunrise, color: "#FFB454", patch: { bedroomCurtains: true, bedroomLights: true, livingLights: true, livingAc: false, livingCurtains: true } },
    { id: "night", label: "Good Night", icon: Moon, color: "#1E90FF", patch: { bedroomLights: false, livingLights: false, bedroomCurtains: false, livingCurtains: false, doorLocked: true, bedroomAc: true, camera: true } },
    { id: "movie", label: "Movie Night", icon: Film, color: "#1BB6F0", patch: { livingLights: false, livingCurtains: false, livingTv: true, livingAc: true, acTemp: 22 } },
    { id: "leave", label: "Leaving Home", icon: LogOut, color: "#EF4444", patch: { livingLights: false, bedroomLights: false, livingTv: false, livingAc: false, bedroomAc: false, livingCurtains: false, bedroomCurtains: false, doorLocked: true, camera: true } },
    { id: "welcome", label: "Welcome Home", icon: HomeIcon, color: "#1BB6F0", patch: { entranceLight: true, livingLights: true, doorLocked: false, livingAc: true, acTemp: 24 } },
];

const DEVICE_TOGGLES = [
    { key: "livingLights", label: "Living Room Lights", icon: Lightbulb, room: "Living Room" },
    { key: "livingTv", label: "TV", icon: Tv, room: "Living Room" },
    { key: "livingAc", label: "Living Room AC", icon: Snowflake, room: "Living Room" },
    { key: "livingFan", label: "Living Room Fan", icon: Fan, room: "Living Room" },
    { key: "livingCurtains", label: "Living Curtains", icon: Blinds, room: "Living Room" },
    { key: "bedroomLights", label: "Bedroom Lights", icon: Lightbulb, room: "Bedroom" },
    { key: "bedroomAc", label: "Bedroom AC", icon: Snowflake, room: "Bedroom" },
    { key: "bedroomFan", label: "Bedroom Fan", icon: Fan, room: "Bedroom" },
    { key: "bedroomCurtains", label: "Bedroom Curtains", icon: Blinds, room: "Bedroom" },
    { key: "doorLocked", label: "Front Door", icon: Lock, room: "Entrance", inverted: true },
    { key: "camera", label: "Security Camera", icon: Camera, room: "Entrance" },
    { key: "entranceLight", label: "Porch Light", icon: Lightbulb, room: "Entrance" },
];

export default function ExperienceSmartLiving() {
    usePageTitle("Experience Smart Living");
    const [state, setState] = useState(initialState);
    const [log, setLog] = useState([{ t: nowStr(), m: "System online · All rooms connected", c: "#1BB6F0" }]);
    const [assistant, setAssistant] = useState("alexa");
    const [selectedCmd, setSelectedCmd] = useState(COMMANDS[0].id);
    const [listening, setListening] = useState(false);
    const [confirm, setConfirm] = useState(null);

    const patch = useCallback((p, label) => {
        setState((prev) => ({ ...prev, ...p }));
        if (label) setLog((prev) => [{ t: nowStr(), m: label, c: "#1BB6F0" }, ...prev].slice(0, 30));
    }, []);

    const toggleDevice = (dev) => {
        const value = state[dev.key];
        const newValue = !value;
        patch({ [dev.key]: newValue }, `${dev.label} → ${labelState(dev, newValue)}`);
    };

    const runCommand = () => {
        const cmd = COMMANDS.find((c) => c.id === selectedCmd);
        if (!cmd) return;
        const asst = ASSISTANTS.find((a) => a.id === assistant);
        setListening(true);
        setConfirm(null);
        setTimeout(() => {
            setListening(false);
            patch(cmd.patch, `${asst.name}: "${cmd.label}"`);
            setConfirm(`Done. ${cmd.label}.`);
            setTimeout(() => setConfirm(null), 2600);
        }, 900);
    };

    const runScene = (sc) => {
        patch(sc.patch, `Scene → ${sc.label}`);
        setConfirm(`Scene activated: ${sc.label}`);
        setTimeout(() => setConfirm(null), 2400);
    };

    const activeCount = useMemo(() => {
        const rooms = { "Living Room": 0, Bedroom: 0, Entrance: 0 };
        DEVICE_TOGGLES.forEach((d) => {
            const on = d.inverted ? !state[d.key] : state[d.key];
            if (on) rooms[d.room] = (rooms[d.room] || 0) + 1;
        });
        return rooms;
    }, [state]);

    return (
        <div className="bg-[#050B24] min-h-[calc(100vh-64px)] text-white" data-testid="experience-page">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Experience Smart Living</div>
                        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" data-testid="experience-title">Your GoSmartNest home</h1>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-white/60">
                        <span className="w-2 h-2 rounded-full bg-[#1BB6F0] animate-soft-pulse" /> All systems operational
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Left: Voice + Scenes */}
                    <div className="lg:col-span-3 order-2 lg:order-1 space-y-4">
                        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 backdrop-blur-md">
                            <div className="text-[10px] uppercase tracking-widest text-[#1BB6F0] font-semibold">Control with</div>
                            <div className="mt-3">
                                <Select value={assistant} onValueChange={setAssistant}>
                                    <SelectTrigger className="bg-white/[0.06] border-white/15 text-white" data-testid="assistant-select">
                                        <SelectValue placeholder="Choose assistant" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ASSISTANTS.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="mt-3">
                                <Select value={selectedCmd} onValueChange={setSelectedCmd}>
                                    <SelectTrigger className="bg-white/[0.06] border-white/15 text-white" data-testid="command-select">
                                        <SelectValue placeholder="Choose command" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-80">
                                        {COMMANDS.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <button
                                onClick={runCommand}
                                data-testid="run-command-btn"
                                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1BB6F0] hover:bg-[#1E90FF] text-white rounded-full px-5 py-3 font-medium transition-colors"
                            >
                                <Mic className="w-4 h-4" /> {listening ? "Listening…" : "Say it"}
                            </button>
                            <AnimatePresence>
                                {listening && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="mt-3 text-xs text-white/70 font-mono"
                                    >
                                        {ASSISTANTS.find((a) => a.id === assistant).trigger} {COMMANDS.find((c) => c.id === selectedCmd)?.label}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                            <div className="text-[10px] uppercase tracking-widest text-[#1BB6F0] font-semibold flex items-center gap-2"><Sparkles className="w-3 h-3" /> Scenes</div>
                            <div className="mt-3 grid grid-cols-2 gap-2">
                                {SCENES.map((sc) => (
                                    <button
                                        key={sc.id}
                                        onClick={() => runScene(sc)}
                                        data-testid={`scene-${sc.id}`}
                                        className="group text-left rounded-xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[#1BB6F0]/60 p-3 transition-colors"
                                    >
                                        <sc.icon className="w-4 h-4 mb-2" style={{ color: sc.color }} />
                                        <div className="text-xs font-medium">{sc.label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Center: 3D House */}
                    <div className="lg:col-span-6 order-1 lg:order-2">
                        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0D1A47] to-[#050B24] border border-white/10 h-[62vh] min-h-[380px] max-h-[560px] sm:h-[520px] lg:h-[620px]">
                            <Suspense fallback={<div className="p-6 text-white/60 text-sm">Loading 3D scene…</div>}>
                                <HouseScene state={state} />
                            </Suspense>
                            {/* Overlay confirmation */}
                            <AnimatePresence>
                                {confirm && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 6 }}
                                        className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white text-[#0B1440] px-4 py-2 rounded-full text-sm font-medium shadow-xl"
                                        data-testid="confirm-toast"
                                    >
                                        {confirm}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            {/* Status pill */}
                            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/40 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 text-xs">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#1BB6F0] animate-soft-pulse" /> Live simulation
                            </div>
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-3">
                            {["Living Room", "Bedroom", "Entrance"].map((r) => (
                                <div key={r} className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
                                    <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">{r}</div>
                                    <div className="text-sm font-semibold mt-1">{activeCount[r] || 0} devices active</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: Devices + Activity */}
                    <div className="lg:col-span-3 order-3 space-y-4">
                        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 max-h-[420px] overflow-y-auto">
                            <div className="text-[10px] uppercase tracking-widest text-[#1BB6F0] font-semibold">Devices</div>
                            <div className="mt-3 space-y-1.5">
                                {DEVICE_TOGGLES.map((d) => {
                                    const on = d.inverted ? !state[d.key] : state[d.key];
                                    return (
                                        <button
                                            key={d.key}
                                            onClick={() => toggleDevice(d)}
                                            data-testid={`device-toggle-${d.key}`}
                                            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                                                on
                                                    ? "bg-[#1BB6F0]/15 border-[#1BB6F0]/40 text-white"
                                                    : "bg-white/[0.03] border-white/10 text-white/75 hover:bg-white/[0.06]"
                                            }`}
                                        >
                                            <span className="flex items-center gap-2 text-sm">
                                                {d.key === "doorLocked" ? (
                                                    state[d.key] ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />
                                                ) : (
                                                    <d.icon className="w-4 h-4" />
                                                )}
                                                {d.label}
                                            </span>
                                            <span className={`text-[10px] font-mono uppercase ${on ? "text-[#1BB6F0]" : "text-white/40"}`}>
                                                {labelState(d, state[d.key])}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 h-[240px] overflow-y-auto">
                            <div className="text-[10px] uppercase tracking-widest text-[#1BB6F0] font-semibold">Activity</div>
                            <ul className="mt-3 space-y-2.5">
                                {log.map((l, i) => (
                                    <li key={i} className="flex items-start gap-2.5 text-xs">
                                        <span className="w-1.5 h-1.5 mt-1.5 rounded-full shrink-0" style={{ background: l.c }} />
                                        <div>
                                            <div className="font-mono text-white/50">{l.t}</div>
                                            <div className="text-white/85">{l.m}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="mt-10 rounded-3xl bg-gradient-to-r from-[#1BB6F0] to-[#1E90FF] p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                    <div>
                        <div className="text-white/80 text-xs tracking-widest uppercase">Ready?</div>
                        <div className="text-2xl sm:text-3xl font-semibold text-white mt-1">This is what your home could feel like.</div>
                    </div>
                    <div className="flex gap-3">
                        <a href="/contact" className="inline-flex items-center gap-1.5 bg-white text-[#1B2E7A] rounded-full px-6 py-3 font-medium hover:bg-white/90 transition-colors" data-testid="exp-cta-consult">Get Free Consultation</a>
                        <a href="/calculator" className="inline-flex items-center gap-1.5 bg-white/10 border border-white/30 text-white rounded-full px-6 py-3 font-medium hover:bg-white/15 transition-colors" data-testid="exp-cta-calc">Calculate cost</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

function nowStr() {
    const d = new Date();
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function labelState(dev, v) {
    if (dev.key === "doorLocked") return v ? "LOCKED" : "UNLOCKED";
    if (dev.key.includes("Curtains")) return v ? "OPEN" : "CLOSED";
    return v ? "ON" : "OFF";
}
